import {
  useConfirmSession,
  useGetSessionsByEventId,
} from '@sunrise-ui/api-client';
import { SessionStatus, type SessionOutput } from '@sunrise-ui/api/events/api';
import {
  Button,
  ButtonVariants,
  TextLight,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

export type SessionConfirmButtonProps = {
  sessionId: string;
  eventId: string; // reserved for potential future use (analytics/logging)
  communityId: string;
  style?: ViewStyle;
};

export function SessionConfirmButton({
  sessionId,
  eventId,
  communityId,
  style,
}: SessionConfirmButtonProps) {
  const styles = useDynamicStyles(createStyles);
  const { mutate: confirmSession, isPending: isConfirming } =
    useConfirmSession();
  const { data: eventSessions } = useGetSessionsByEventId(
    eventId,
    false,
    communityId,
  );

  const session = useMemo(
    () => eventSessions?.find((s: SessionOutput) => s.id === sessionId),
    [eventSessions, sessionId],
  );

  const isConfirmed = session?.status === SessionStatus.Confirmed;

  // Keep loading state visible after click until status flips to Confirmed
  const [hasRequestedConfirm, setHasRequestedConfirm] = useState(false);

  useEffect(() => {
    if (isConfirmed && hasRequestedConfirm) {
      setHasRequestedConfirm(false);
    }
  }, [isConfirmed, hasRequestedConfirm]);

  const handleConfirm = async () => {
    setHasRequestedConfirm(true);
    confirmSession(
      { sessionId },
      {
        onSettled: () => {
          setHasRequestedConfirm(false);
        },
      },
    );
  };

  if (isConfirmed) {
    return (
      <View style={[styles.confirmedBadge, style]}>
        <TextLight>🎉 Confirmed!</TextLight>
      </View>
    );
  }

  // Show loading if mutation in-flight OR if we already requested confirm but cache hasn't reflected yet
  const isLoadingConfirm =
    isConfirming || (hasRequestedConfirm && !isConfirmed);

  return (
    <Button
      onPress={handleConfirm}
      disabled={isLoadingConfirm}
      loading={isLoadingConfirm}
      variant={ButtonVariants.PINK}
      size="small"
      style={style}
    >
      Confirm
    </Button>
  );
}

const createStyles = () =>
  StyleSheet.create({
    confirmedBadge: {
      display: 'flex',
      flexDirection: 'row',
      gap: 4,
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: 32,
    },
  });
